import type { GastoMensualRow } from '@/services/gastosReporteService'
import type { Periodo } from '@/lib/periodo-reporte'

/** Total de una categoría de gasto en el período. `color` es el color guardado en la categoría. */
export interface EgresoCategoria {
  /** Clave de agrupamiento: 'id:<categoria_id>', o 'nombre:<categoria>' si la fila no trae id. */
  clave: string
  categoria: string
  color: string | null
  total: number
}

const esEgresoEnPesos = (r: GastoMensualRow): boolean =>
  r.categoria_tipo === 'gasto' && (!r.moneda || r.moneda === 'ARS')

/**
 * Filas de gastos en ARS de categorías de tipo 'gasto' de los meses que toca el período.
 * Deja afuera los retiros. La vista agrupa por mes, así que cada mes entra completo.
 */
const egresosDelPeriodo = (rows: GastoMensualRow[], periodo: Periodo): GastoMensualRow[] => {
  const desde = periodo.desde.slice(0, 7)
  const hasta = periodo.hasta.slice(0, 7)
  return rows.filter(esEgresoEnPesos).filter((r) => r.mes_anio >= desde && r.mes_anio <= hasta)
}

/** Suma los egresos del período. */
export const sumarEgresos = (rows: GastoMensualRow[], periodo: Periodo): number =>
  egresosDelPeriodo(rows, periodo).reduce((acc, r) => acc + r.total, 0)

/** Egresos del período por categoría, de mayor a menor. Las categorías suman lo mismo que `sumarEgresos`. */
export const egresosPorCategoria = (rows: GastoMensualRow[], periodo: Periodo): EgresoCategoria[] => {
  const porCategoria = new Map<string, EgresoCategoria>()
  for (const r of egresosDelPeriodo(rows, periodo)) {
    const clave = r.categoria_id ? `id:${r.categoria_id}` : `nombre:${r.categoria}`
    const prev = porCategoria.get(clave)
    porCategoria.set(clave, { clave, categoria: r.categoria, color: r.color, total: (prev?.total ?? 0) + r.total })
  }
  return [...porCategoria.values()].sort((a, b) => b.total - a.total)
}

/** Primer día del primer mes con gastos en ARS de tipo 'gasto', en formato 'YYYY-MM-DD', o null si no hay. */
export const inicioEgresos = (rows: GastoMensualRow[]): string | null => {
  const meses = rows.filter(esEgresoEnPesos).map((r) => r.mes_anio)
  if (meses.length === 0) return null
  return `${meses.reduce((min, m) => (m < min ? m : min))}-01`
}
